import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Layout = ({ children }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigationItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Our Team', path: '/staff' },
    { name: 'Strategic Consulting & Management', path: '/strategic-consulting' },
    { name: 'Investments & Lending', path: '/investments-lending' },
    { name: 'Construction & Real Estate', path: '/construction-real-estate' },
    { name: 'Hospitality & Media', path: '/hospitality' },
    { name: 'Research & Global Operations', path: '/research' },
    { name: 'Intelligent Financing & Crypto', path: '/intelligent-financing' },
    { name: 'Precious Metals Trade', path: '/precious-metals' },
    { name: 'Asset Administration & Management', path: '/asset-administration' },
    { name: 'Contact Us', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation */}
      <nav className="glass-card sticky top-0 z-50 border-b-2 gold-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-4">
              <img 
                src="/logo.jpg" 
                alt="Vajreya Ventures" 
                className="h-16 w-auto rounded-lg shadow-lg"
              />
              <div className="hidden md:block">
                <h1 className="text-2xl font-bold text-primary">Vajreya Ventures</h1>
                <p className="text-sm gold-text">Private Limited</p>
              </div>
            </Link>

            {/* Hamburger Menu Button - Top Right */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-md text-primary hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              <svg
                className="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {sidebarOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar - Slides in from right */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-6 border-b-2 gold-border bg-primary">
            <h2 className="text-xl font-bold text-white">Menu</h2>
            <button
              onClick={closeSidebar}
              className="p-2 rounded-md text-white hover:bg-white hover:bg-opacity-20 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Navigation Links */}
          <div className="flex-1 overflow-y-auto py-4">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeSidebar}
                className={`block px-6 py-3 text-base font-medium transition-colors border-l-4 ${
                  isActive(item.path)
                    ? 'bg-primary bg-opacity-10 border-gold text-primary'
                    : 'border-transparent text-gray-700 hover:bg-gray-50 hover:border-primary'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Sidebar Footer - Login Button */}
          <div className="p-6 border-t-2 gold-border">
            <Link
              to="/login"
              onClick={closeSidebar}
              className="w-full px-6 py-3 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors text-base font-medium flex items-center justify-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              Log In
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="glass-card border-t-2 gold-border mt-12">
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Company Info */}
            <div>
              <img 
                src="/logo.jpg" 
                alt="Vajreya Ventures" 
                className="h-20 w-auto mb-4 rounded-lg"
              />
              <p className="text-sm text-gray-600">
                Vajreya Ventures Private Limited - A dynamic investment, hospitality and real estate firm.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-bold text-primary mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-sm text-gray-600 hover:text-primary transition-colors">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-sm text-gray-600 hover:text-primary transition-colors">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/staff" className="text-sm text-gray-600 hover:text-primary transition-colors">
                    Our Team
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-sm text-gray-600 hover:text-primary transition-colors">
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-bold text-primary mb-4">Contact Us</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>Bangalore, Karnataka, India</li>
                <li>CEO: Mayoor Shastry BN</li>
                <li className="pt-4">
                  <Link
                    to="/contact"
                    className="inline-block px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
                  >
                    Get in Touch
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t gold-border mt-8 pt-8 text-center">
            <p className="text-sm text-gray-600">
              &copy; {new Date().getFullYear()} Vajreya Ventures Private Limited. All rights reserved.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              GSTIN: 29AAKCV2897F1ZO
            </p>
            <p className="text-xs text-gray-500 mt-1">
              vajreyaventures.com
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
